import { useState, useRef, useCallback } from 'react';
import { Camera, Image as ImageIcon, ArrowLeft, RotateCcw, Check, ChevronRight, User } from 'lucide-react';
import { compressImage, fileToPreviewUrl } from '@/utils/imageUtils';
import { toast } from 'sonner';

const PHOTO_STEPS = [
  {
    key: 'front',
    label: 'Foto de frente',
    description: 'Olhe diretamente para a câmara',
    icon: 'front',
    example: '/selfie-example-front.webp',
  },
  {
    key: 'left',
    label: 'Lado esquerdo',
    description: 'Vire a cabeça ligeiramente para a esquerda',
    icon: 'left',
    example: '/selfie-example-left.webp',
  },
  {
    key: 'right',
    label: 'Lado direito',
    description: 'Vire a cabeça ligeiramente para a direita',
    icon: 'right',
    example: '/selfie-example-right.webp',
  },
];

function FaceGuideIcon({ direction, size = 48 }) {
  const rotation = direction === 'left' ? -25 : direction === 'right' ? 25 : 0;
  return (
    <div
      className="rounded-full flex items-center justify-center"
      style={{
        width: size,
        height: size,
        background: 'rgba(181,152,90,0.12)',
        border: '2px solid rgba(181,152,90,0.3)',
      }}
    >
      <User
        size={size * 0.5}
        strokeWidth={1.5}
        style={{ color: '#B5985A', transform: `rotateY(${rotation}deg)` }}
      />
    </div>
  );
}

export default function PhotoCapture({ onCapture, onBack }) {
  const [step, setStep] = useState(0); // 0=intro, 1-3=capture, 4=review
  const [photos, setPhotos] = useState([null, null, null]); // [{blob, previewUrl}, ...]
  const [captureMode, setCaptureMode] = useState('choose'); // 'choose' | 'camera'
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const currentPhotoIndex = step - 1; // 0, 1, 2 for the 3 photos
  const currentStepInfo = PHOTO_STEPS[currentPhotoIndex];

  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((t) => t.stop());
      setCameraStream(null);
    }
  }, [cameraStream]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1024 } },
      });
      setCameraStream(stream);
      setCaptureMode('camera');
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch {
      toast.error('Não foi possível aceder à câmara. Tente a galeria.');
    }
  };

  const captureFromCamera = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(
      async (blob) => {
        if (!blob) return;
        stopCamera();
        try {
          const compressed = await compressImage(blob, 1024, 0.8);
          const previewUrl = URL.createObjectURL(compressed);
          const newPhotos = [...photos];
          if (newPhotos[currentPhotoIndex]?.previewUrl) {
            URL.revokeObjectURL(newPhotos[currentPhotoIndex].previewUrl);
          }
          newPhotos[currentPhotoIndex] = { blob: compressed, previewUrl };
          setPhotos(newPhotos);
          setCaptureMode('choose');
          // Auto-advance to next step or review
          if (currentPhotoIndex < 2) {
            setStep(step + 1);
          } else {
            setStep(4);
          }
        } catch {
          toast.error('Erro ao processar foto.');
        }
      },
      'image/jpeg',
      0.9
    );
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset file input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
    try {
      const compressed = await compressImage(file, 1024, 0.8);
      const previewUrl = fileToPreviewUrl(compressed);
      const newPhotos = [...photos];
      if (newPhotos[currentPhotoIndex]?.previewUrl) {
        URL.revokeObjectURL(newPhotos[currentPhotoIndex].previewUrl);
      }
      newPhotos[currentPhotoIndex] = { blob: compressed, previewUrl };
      setPhotos(newPhotos);
      // Auto-advance
      if (currentPhotoIndex < 2) {
        setStep(step + 1);
      } else {
        setStep(4);
      }
    } catch {
      toast.error('Erro ao processar imagem. Tente outra.');
    }
  };

  const handleRetakePhoto = (index) => {
    if (photos[index]?.previewUrl) {
      URL.revokeObjectURL(photos[index].previewUrl);
    }
    const newPhotos = [...photos];
    newPhotos[index] = null;
    setPhotos(newPhotos);
    setStep(index + 1);
    setCaptureMode('choose');
  };

  const handleConfirmAll = () => {
    const blobs = photos.map((p) => p.blob);
    onCapture(blobs);
  };

  const handleBack = () => {
    stopCamera();
    photos.forEach((p) => { if (p?.previewUrl) URL.revokeObjectURL(p.previewUrl); });
    onBack();
  };

  const handleStepBack = () => {
    stopCamera();
    setCaptureMode('choose');
    if (step <= 1) {
      setStep(0);
    } else {
      setStep(step - 1);
    }
  };

  // ── INTRO SCREEN ──
  if (step === 0) {
    return (
      <div className="px-6 py-8 animate-fade-in" data-testid="photo-capture-intro">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 mb-6 transition-colors"
          style={{ color: '#A3A3A3' }}
          data-testid="photo-capture-back-btn"
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
          <span className="font-body text-sm">Voltar</span>
        </button>

        <h2 className="font-headline text-xl mb-2 text-white" data-testid="photo-capture-title">
          A sua foto no kart
        </h2>
        <p className="font-body text-sm mb-6" style={{ color: '#A3A3A3' }}>
          Precisamos de 3 fotos suas para um resultado perfeito
        </p>

        {/* Example result */}
        <div
          className="rounded-xl overflow-hidden mb-6"
          style={{
            border: '1px solid rgba(181,152,90,0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          }}
        >
          <img
            src="/example-kart.webp"
            alt="Exemplo de resultado"
            className="w-full"
            data-testid="photo-capture-example"
          />
          <p
            className="font-body text-center py-2"
            style={{ fontSize: '0.75rem', color: '#B5985A', background: '#1A1A1A' }}
          >
            O resultado final será algo assim
          </p>
        </div>

        {/* 3 photo steps guide */}
        <div className="space-y-3 mb-6">
          {PHOTO_STEPS.map((s, i) => (
            <div
              key={s.key}
              className="flex items-center gap-3 p-3 rounded-lg"
              style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.05)' }}
              data-testid={`photo-step-guide-${i}`}
            >
              <FaceGuideIcon direction={s.icon} size={40} />
              <div className="flex-1">
                <p className="font-subtitle text-sm text-white">{s.label}</p>
                <p className="font-body text-xs" style={{ color: '#A3A3A3' }}>{s.description}</p>
              </div>
              <span
                className="font-cta text-xs px-2 py-1 rounded"
                style={{ background: 'rgba(181,152,90,0.12)', color: '#B5985A' }}
              >
                {i + 1}/3
              </span>
            </div>
          ))}
        </div>

        <button
          onClick={() => setStep(1)}
          className="btn-gold w-full"
          data-testid="photo-capture-start-btn"
        >
          Começar
          <ChevronRight size={18} strokeWidth={1.5} />
        </button>
      </div>
    );
  }

  // ── CAMERA MODE ──
  if (captureMode === 'camera' && step >= 1 && step <= 3) {
    return (
      <div className="px-6 py-8 animate-fade-in" data-testid="photo-camera-screen">
        {/* Step indicator */}
        <div className="flex items-center justify-between mb-4">
          <span className="font-subtitle text-xs" style={{ color: '#B5985A', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            {currentStepInfo.label} — Foto {step} de 3
          </span>
        </div>

        {/* Progress */}
        <div className="progress-bar mb-4">
          <div className="progress-bar-fill" style={{ width: `${(step / 3) * 100}%` }} />
        </div>

        <p className="font-body text-sm text-center mb-4" style={{ color: '#A3A3A3' }}>
          {currentStepInfo.description}
        </p>

        <div
          className="relative rounded-xl overflow-hidden mb-6"
          style={{ border: '2px solid #B5985A', aspectRatio: '3/4' }}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{ transform: 'scaleX(-1)' }}
            data-testid="photo-camera-viewfinder"
          />
          {/* Guide overlay */}
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div style={{ opacity: 0.2 }}>
              <FaceGuideIcon direction={currentStepInfo.icon} size={120} />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-6">
          <button
            onClick={() => { stopCamera(); setCaptureMode('choose'); }}
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.1)' }}
            data-testid="photo-camera-cancel-btn"
          >
            <ArrowLeft size={20} style={{ color: '#fff' }} />
          </button>

          <button
            onClick={captureFromCamera}
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{
              background: '#B5985A',
              boxShadow: '0 0 0 4px rgba(181,152,90,0.3), 0 0 20px rgba(181,152,90,0.3)',
            }}
            data-testid="photo-camera-capture-btn"
          >
            <Camera size={24} style={{ color: '#1A1A1A' }} />
          </button>

          <div className="w-12 h-12" />
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </div>
    );
  }

  // ── CAPTURE STEP (choose mode) ──
  if (step >= 1 && step <= 3 && captureMode === 'choose') {
    return (
      <div className="px-6 py-8 animate-fade-in" data-testid="photo-capture-step-screen">
        <button
          onClick={handleStepBack}
          className="flex items-center gap-2 mb-4 transition-colors"
          style={{ color: '#A3A3A3' }}
          data-testid="photo-step-back-btn"
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
          <span className="font-body text-sm">Voltar</span>
        </button>

        {/* Step indicator */}
        <div className="flex items-center justify-between mb-2">
          <span className="font-subtitle text-xs" style={{ color: '#B5985A', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Foto {step} de 3
          </span>
        </div>

        {/* Progress */}
        <div className="progress-bar mb-6">
          <div className="progress-bar-fill" style={{ width: `${(step / 3) * 100}%` }} />
        </div>

        {/* Example photo guide */}
        <div className="text-center mb-6">
          <div
            className="mx-auto mb-3 rounded-xl overflow-hidden"
            style={{
              width: 160,
              aspectRatio: '3/4',
              border: '2px solid rgba(181,152,90,0.3)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            }}
          >
            <img
              src={currentStepInfo.example}
              alt={`Exemplo: ${currentStepInfo.label}`}
              className="w-full h-full object-cover"
              data-testid="photo-step-example"
              loading="eager"
            />
          </div>
          <p
            className="font-subtitle text-xs mb-2"
            style={{ color: '#B5985A', letterSpacing: '0.08em', textTransform: 'uppercase' }}
          >
            Exemplo
          </p>
          <h3 className="font-headline text-lg text-white mb-1" data-testid="photo-step-label">
            {currentStepInfo.label}
          </h3>
          <p className="font-body text-sm" style={{ color: '#A3A3A3' }}>
            {currentStepInfo.description}
          </p>
        </div>

        {/* Captured photos so far (small thumbnails) */}
        {photos.some((p) => p !== null) && (
          <div className="flex justify-center gap-2 mb-6">
            {photos.map((p, i) => (
              <div
                key={i}
                className="w-14 h-14 rounded-lg overflow-hidden flex items-center justify-center"
                style={{
                  border: `2px solid ${i === currentPhotoIndex ? '#B5985A' : p ? 'rgba(76,175,80,0.5)' : 'rgba(255,255,255,0.1)'}`,
                  background: '#1A1A1A',
                }}
                data-testid={`photo-thumbnail-${i}`}
              >
                {p ? (
                  <img src={p.previewUrl} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs" style={{ color: '#666' }}>{i + 1}</span>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={startCamera}
            className="btn-gold w-full"
            data-testid="photo-step-selfie-btn"
          >
            <Camera size={20} strokeWidth={1.5} />
            Tirar Selfie
          </button>

          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn-outline w-full"
            data-testid="photo-step-gallery-btn"
          >
            <ImageIcon size={20} strokeWidth={1.5} />
            Escolher da Galeria
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="user"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="photo-step-file-input"
          />
        </div>
      </div>
    );
  }

  // ── REVIEW ALL 3 PHOTOS ──
  if (step === 4) {
    const allCaptured = photos.every((p) => p !== null);

    return (
      <div className="px-6 py-8 animate-fade-in" data-testid="photo-review-screen">
        <h2 className="font-headline text-xl mb-2 text-white text-center">
          As suas 3 fotos
        </h2>
        <p className="font-body text-sm mb-6 text-center" style={{ color: '#A3A3A3' }}>
          Verifique se está satisfeito com as fotos
        </p>

        <div className="grid grid-cols-3 gap-3 mb-6">
          {photos.map((p, i) => (
            <div key={i} className="relative" data-testid={`photo-review-${i}`}>
              <div
                className="rounded-xl overflow-hidden"
                style={{
                  border: '2px solid rgba(181,152,90,0.3)',
                  aspectRatio: '3/4',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
                }}
              >
                {p ? (
                  <img src={p.previewUrl} alt={PHOTO_STEPS[i].label} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ background: '#1A1A1A' }}>
                    <span className="text-xs" style={{ color: '#666' }}>Em falta</span>
                  </div>
                )}
              </div>
              <p className="font-subtitle text-xs text-center mt-1" style={{ color: '#A3A3A3' }}>
                {PHOTO_STEPS[i].label}
              </p>
              {p && (
                <button
                  onClick={() => handleRetakePhoto(i)}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(255,255,255,0.2)' }}
                  data-testid={`photo-retake-${i}`}
                >
                  <RotateCcw size={12} style={{ color: '#fff' }} />
                </button>
              )}
            </div>
          ))}
        </div>

        {allCaptured && (
          <button
            onClick={handleConfirmAll}
            className="btn-gold w-full mb-3"
            data-testid="photo-review-confirm-btn"
          >
            <Check size={20} strokeWidth={1.5} />
            Gerar a minha foto no kart
          </button>
        )}

        <button
          onClick={() => setStep(0)}
          className="btn-outline w-full"
          data-testid="photo-review-restart-btn"
        >
          <RotateCcw size={18} strokeWidth={1.5} />
          Começar de novo
        </button>
      </div>
    );
  }

  return null;
}
